import React from "react";
import * as Sentry from "@sentry/react";

interface Props {
  children: React.ReactNode;
  /**
   * Optional label rendered in the fallback and tagged on the Sentry
   * report. Use to distinguish wrappers in a multi-pane app — e.g.
   * `<ErrorBoundary label="chart pane">` vs `<ErrorBoundary label="gamma pane">`
   * so a single-pane crash isolates the failure and a Sentry tag tells us
   * which one blew up.
   */
  label?: string;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(`ErrorBoundary${this.props.label ? ` [${this.props.label}]` : ""} caught:`, error, errorInfo);
    const context: { tags?: { errorBoundaryLabel: string }; contexts: { react: { componentStack: string } } } = {
      contexts: { react: { componentStack: errorInfo.componentStack ?? "" } },
    };
    if (this.props.label) context.tags = { errorBoundaryLabel: this.props.label };
    Sentry.captureException(error, context);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const where = this.props.label ? ` (${this.props.label})` : "";
      return <div role="alert">Something went wrong{where}. Please refresh.</div>;
    }
    return this.props.children;
  }
}
